import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Send, MessageCircle, AlertTriangle, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { Btn } from "./common";
import { overlayCloseHandlers } from "../lib/overlayClose";

/**
 * WhatsappComposer - Modal para enviar mensagens WhatsApp via LetsCall One.
 *
 * Props:
 *   open          - boolean
 *   onClose       - callback
 *   numero        - numero pre-preenchido (internacional, ex: "+351912345678")
 *   accountId     - id da conta CRM (para agendar e registar atividade)
 *   contactId     - id do contacto CRM (opcional)
 *   nome          - nome da conta (para o titulo do modal)
 *   onSent        - callback apos envio/agendamento
 */
export default function WhatsappComposer({ open, onClose, numero = "", accountId = "", contactId = "", nome = "", onSent }) {
  const [phone, setPhone] = useState(numero);
  const [tipo, setTipo] = useState("livre");          // "livre" | "template"
  const [mensagem, setMensagem] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loadingTpls, setLoadingTpls] = useState(false);
  const [templateSel, setTemplateSel] = useState("");
  const [templateParams, setTemplateParams] = useState([]);
  const [sending, setSending] = useState(false);
  const [agendarEnvio, setAgendarEnvio] = useState(false);
  const [dataAgendada, setDataAgendada] = useState("");
  const [horaAgendada, setHoraAgendada] = useState("09:00");
  const [showScheduleHint, setShowScheduleHint] = useState(false);

  useEffect(() => {
    if (open) {
      setPhone(numero);
      setMensagem("");
      setTemplateSel("");
      setTemplateParams([]);
      setAgendarEnvio(false);
      setDataAgendada("");
      setHoraAgendada("09:00");
      setTipo("livre");
    }
  }, [open, numero]);

  useEffect(() => {
    if (!open) return;
    setLoadingTpls(true);
    api.get("/channels/letscall/whatsapp/templates")
      .then(({ data }) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTpls(false));
  }, [open]);

  const send = async () => {
    if (!phone.trim()) return toast.error("Numero de telefone obrigatorio.");
    if (tipo === "livre" && !mensagem.trim()) return toast.error("Mensagem obrigatoria.");
    if (tipo === "template" && !templateSel) return toast.error("Escolha um template.");

    const agendado_para = agendarEnvio && dataAgendada
      ? new Date(`${dataAgendada}T${horaAgendada || "09:00"}:00`).toISOString()
      : null;

    setSending(true);
    try {
      if (agendado_para) {
        await api.post("/scheduled-messages", {
          tipo: "whatsapp",
          numero: phone.trim(),
          mensagem: tipo === "livre" ? mensagem.trim() : "",
          template_name: tipo === "template" ? templateSel : "",
          template_params: tipo === "template" ? templateParams : [],
          agendado_para,
          account_id: accountId,
          contact_id: contactId,
        });
        toast.success(`WhatsApp agendado para ${new Date(agendado_para).toLocaleString("pt-PT")}.`);
      } else {
        await api.post("/channels/letscall/whatsapp", {
          to: phone.trim(),
          mensagem: tipo === "livre" ? mensagem.trim() : "",
          template_name: tipo === "template" ? templateSel : "",
          template_params: tipo === "template" ? templateParams : [],
        });
        toast.success("Mensagem WhatsApp enviada.");
      }
      onSent && onSent();
      onClose && onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 422 || detail.toLowerCase().includes("24h")) {
        toast.error("Janela de 24h expirada. Use um template pre-aprovado ou agende para mais tarde.", { duration: 8000 });
      } else {
        toast.error(formatApiErrorDetail(detail) || "Falha ao enviar WhatsApp.");
      }
    } finally { setSending(false); }
  };

  if (!open) return null;

  const templateObj = templates.find((t) => (t.name || t.id || t.nome) === templateSel);
  const paramCount = templateObj ? (templateObj.param_count || templateObj.variables?.length || 0) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4"
      {...overlayCloseHandlers(onClose)} data-testid="whatsapp-composer">
      <div className="bg-white rounded-xl w-full max-w-lg mt-16" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-tint bg-[#25d366]/5">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} className="text-[#25d366]" />
            <h3 className="text-navy font-semibold text-sm">WhatsApp{nome ? ` - ${nome}` : ""}</h3>
          </div>
          <button onClick={onClose} data-testid="whatsapp-composer-close" className="text-slate-500 hover:text-navy"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3 max-h-[calc(100vh-160px)] overflow-y-auto">
          {/* Numero */}
          <div>
            <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Numero WhatsApp</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+351912345678"
              data-testid="whatsapp-composer-numero"
              className="w-full px-3 py-2 text-sm border border-tint rounded font-mono" />
          </div>

          {/* Tipo: Mensagem livre vs Template */}
          <div>
            <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Tipo de mensagem</label>
            <div className="flex rounded-lg border border-tint overflow-hidden">
              <button onClick={() => setTipo("livre")} data-testid="whatsapp-tipo-livre"
                className={`flex-1 py-2 text-xs font-semibold transition ${tipo === "livre" ? "bg-navy text-white" : "text-navy hover:bg-tint"}`}>
                Mensagem livre
              </button>
              <button onClick={() => setTipo("template")} data-testid="whatsapp-tipo-template"
                className={`flex-1 py-2 text-xs font-semibold transition ${tipo === "template" ? "bg-navy text-white" : "text-navy hover:bg-tint"}`}>
                Template Meta
              </button>
            </div>
          </div>

          {/* Aviso 24h - so para mensagem livre */}
          {tipo === "livre" && (
            <div className="flex items-start gap-2 rounded-md p-2.5 bg-amber-50 border border-amber-200 text-[11.5px] text-amber-800"
              data-testid="whatsapp-24h-warning">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                Mensagens livres so podem ser enviadas dentro da <b>janela de 24h</b> apos o ultimo contacto do cliente.
                Fora dessa janela, use um <button type="button" onClick={() => setTipo("template")}
                  className="underline font-semibold cursor-pointer">Template Meta</button> pre-aprovado.
              </span>
            </div>
          )}

          {/* Textarea mensagem livre */}
          {tipo === "livre" && (
            <div>
              <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Mensagem</label>
              <textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={4}
                data-testid="whatsapp-composer-mensagem"
                placeholder="Escreva a sua mensagem..."
                className="w-full px-3 py-2 text-sm border border-tint rounded resize-none focus:outline-none focus:ring-2 focus:ring-[#25d366]/30" />
            </div>
          )}

          {/* Template picker */}
          {tipo === "template" && (
            <div className="space-y-2">
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1 uppercase">Template</label>
                {loadingTpls ? (
                  <div className="text-xs text-muted2 italic py-1">A carregar templates...</div>
                ) : templates.length === 0 ? (
                  <div className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5" data-testid="whatsapp-no-templates">
                    Sem templates aprovados. Configure e aprove templates no portal LetsCall One (Meta Business).
                  </div>
                ) : (
                  <select value={templateSel} onChange={(e) => { setTemplateSel(e.target.value); setTemplateParams([]); }}
                    data-testid="whatsapp-template-select"
                    className="w-full px-3 py-2 text-sm border border-tint rounded bg-white">
                    <option value="">-- escolher template --</option>
                    {templates.map((t, i) => (
                      <option key={i} value={t.name || t.id || t.nome}>
                        {t.name || t.nome || t.id}{t.language ? ` (${t.language})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {/* Parametros do template */}
              {templateSel && paramCount > 0 && (
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-navy uppercase">Parametros do template</label>
                  {Array.from({ length: paramCount }).map((_, pi) => (
                    <input key={pi} placeholder={`{{${pi + 1}}}`}
                      value={templateParams[pi] || ""}
                      onChange={(e) => {
                        const np = [...templateParams];
                        np[pi] = e.target.value;
                        setTemplateParams(np);
                      }}
                      data-testid={`whatsapp-template-param-${pi}`}
                      className="w-full px-3 py-1.5 text-sm border border-tint rounded" />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Agendar envio */}
          <div className="border border-tint rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => { setAgendarEnvio(!agendarEnvio); setShowScheduleHint(false); }}
              data-testid="whatsapp-agendar-toggle"
              className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold transition ${agendarEnvio ? "bg-blue/5 text-blue" : "text-navy hover:bg-tint"}`}>
              <span className="flex items-center gap-1.5"><Clock size={13} /> Agendar envio</span>
              {agendarEnvio ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {agendarEnvio && (
              <div className="px-3 pb-3 pt-1 space-y-2 border-t border-tint bg-blue/5">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-semibold text-navy mb-1 uppercase">Data</label>
                    <input type="date" value={dataAgendada} onChange={(e) => setDataAgendada(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      data-testid="whatsapp-agendar-data"
                      className="w-full px-2 py-1.5 text-sm border border-tint rounded" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-navy mb-1 uppercase">Hora</label>
                    <input type="time" value={horaAgendada} onChange={(e) => setHoraAgendada(e.target.value)}
                      data-testid="whatsapp-agendar-hora"
                      className="w-full px-2 py-1.5 text-sm border border-tint rounded" />
                  </div>
                </div>
                <p className="text-[10.5px] text-muted2">A mensagem sera enviada automaticamente na data/hora escolhida.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-tint bg-slate-50">
          <Btn variant="ghost" onClick={onClose} testid="whatsapp-composer-cancel">Cancelar</Btn>
          <Btn
            onClick={send}
            disabled={sending || !phone.trim() || (tipo === "livre" && !mensagem.trim()) || (tipo === "template" && !templateSel) || (agendarEnvio && !dataAgendada)}
            testid="whatsapp-composer-send"
            className="bg-[#25d366] hover:bg-[#1da355] text-white">
            <MessageCircle size={13} />
            {sending ? "A enviar..." : agendarEnvio ? "Agendar WhatsApp" : "Enviar WhatsApp"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
